import React, { useEffect, useState } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  FireIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { differentialDiagnosisApi } from '../../api/differentialDiagnosis';

interface HealthInvestigationPanelProps {
  birds: Array<{ id: string; bandId?: string; species?: { commonName?: string } }>;
  onStartInvestigation: (birdId: string, birdLabel: string) => void;
  onStartFromChecklist: (birdId: string, birdLabel: string, itemIds: string[]) => void;
}

export default function HealthInvestigationPanel({
  birds,
  onStartInvestigation,
}: HealthInvestigationPanelProps) {
  const [selectedBirdId, setSelectedBirdId] = useState('');
  const [protocol, setProtocol] = useState<Array<{ phase: string; title: string; description: string }>>([]);

  useEffect(() => {
    differentialDiagnosisApi.getCatalog().then((c) => setProtocol(c.protocol)).catch(() => {});
  }, []);

  const start = () => {
    const bird = birds.find((b) => b.id === selectedBirdId);
    if (!bird) return;
    const label = bird.bandId || bird.species?.commonName || bird.id;
    onStartInvestigation(bird.id, label);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-6">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-cyan-100 p-3">
            <MagnifyingGlassIcon className="h-8 w-8 text-cyan-700" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">Symptom-first investigation</h3>
            <p className="mt-1 text-sm text-slate-600">
              You see clinical signs but may not know the disease. This follows problem-oriented avian medicine:
              observe symptoms → build a problem list → rank differentials → plan diagnostics → start a care workflow.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <select
                value={selectedBirdId}
                onChange={(e) => setSelectedBirdId(e.target.value)}
                className="input min-w-[200px] text-sm"
              >
                <option value="">Select affected bird</option>
                {birds.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bandId || b.id} {b.species?.commonName ? `· ${b.species.commonName}` : ''}
                  </option>
                ))}
              </select>
              <button type="button" onClick={start} disabled={!selectedBirdId} className="btn btn-primary text-sm">
                <PlusIcon className="h-4 w-4" />
                Start investigation
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {protocol.map((phase) => (
          <div key={phase.phase} className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-cyan-600">{phase.phase.replace(/_/g, ' ')}</p>
            <p className="mt-1 font-medium text-slate-900">{phase.title}</p>
            <p className="mt-1 text-sm text-slate-500">{phase.description}</p>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <FireIcon className="h-5 w-5 shrink-0 text-amber-600" />
        <p>
          Emergency signs (severe breathing difficulty, bleeding, collapse, seizures) trigger immediate triage guidance.
          Always involve an avian veterinarian for definitive diagnosis.
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-slate-50 p-4 text-sm text-slate-600">
        <ClipboardDocumentListIcon className="h-5 w-5 shrink-0 text-slate-400" />
        <p>
          Checklists with concerns can launch an investigation pre-filled with mapped symptoms — see the Checklists tab.
        </p>
      </div>
    </div>
  );
}
